import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";

const initialState = {
  personnelEmergencyContacts: {},
  loading: false,
  error: null,
};

const FETCH_EMERGENCY_CONTACTS_REQUEST = "FETCH_EMERGENCY_CONTACTS_REQUEST";
const FETCH_EMERGENCY_CONTACTS_SUCCESS = "FETCH_EMERGENCY_CONTACTS_SUCCESS";
const FETCH_EMERGENCY_CONTACTS_FAILURE = "FETCH_EMERGENCY_CONTACTS_FAILURE";
const CREATE_EMERGENCY_CONTACT_REQUEST = "CREATE_EMERGENCY_CONTACT_REQUEST";
const CREATE_EMERGENCY_CONTACT_SUCCESS = "CREATE_EMERGENCY_CONTACT_SUCCESS";
const CREATE_EMERGENCY_CONTACT_FAILURE = "CREATE_EMERGENCY_CONTACT_FAILURE";
const UPDATE_EMERGENCY_CONTACT_REQUEST = "UPDATE_EMERGENCY_CONTACT_REQUEST";
const UPDATE_EMERGENCY_CONTACT_SUCCESS = "UPDATE_EMERGENCY_CONTACT_SUCCESS";
const UPDATE_EMERGENCY_CONTACT_FAILURE = "UPDATE_EMERGENCY_CONTACT_FAILURE";
const DELETE_EMERGENCY_CONTACT_REQUEST = "DELETE_EMERGENCY_CONTACT_REQUEST";
const DELETE_EMERGENCY_CONTACT_SUCCESS = "DELETE_EMERGENCY_CONTACT_SUCCESS";
const DELETE_EMERGENCY_CONTACT_FAILURE = "DELETE_EMERGENCY_CONTACT_FAILURE";
const UPDATE_BULK_EMERGENCY_CONTACTS_REQUEST = "UPDATE_BULK_EMERGENCY_CONTACTS_REQUEST";
const UPDATE_BULK_EMERGENCY_CONTACTS_SUCCESS = "UPDATE_BULK_EMERGENCY_CONTACTS_SUCCESS";
const UPDATE_BULK_EMERGENCY_CONTACTS_FAILURE = "UPDATE_BULK_EMERGENCY_CONTACTS_FAILURE";

export const getPersonnelEmergencyContacts =
  (personnelId, sortField = "id", sortDirection = "asc") =>
  async (dispatch) => {
    const service = getService(serviceNames.perEmergencyContact);
    const query = {
      personnelId,
      $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
      isDeleted: false,
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: FETCH_EMERGENCY_CONTACTS_REQUEST,
        success: FETCH_EMERGENCY_CONTACTS_SUCCESS,
        failure: FETCH_EMERGENCY_CONTACTS_FAILURE,
      },
      apiCall: () => service.find({ query }),
      transformResult: (result) => ({
        personnelId,
        personnelEmergencyContacts: result,
      }),
      errorMessagePrefix: "Failed to fetch personnel emergency contacts",
    });
  };

export const createPersonnelEmergencyContact =
  (emergencyContactData) => async (dispatch) => {
    const service = getService(serviceNames.perEmergencyContact);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: CREATE_EMERGENCY_CONTACT_REQUEST,
        success: CREATE_EMERGENCY_CONTACT_SUCCESS,
        failure: CREATE_EMERGENCY_CONTACT_FAILURE,
      },
      apiCall: () => service.create(emergencyContactData),
      transformResult: (result) => ({
        ...result,
        personnelId: emergencyContactData.personnelId,
      }),
      successMessage: "Personnel emergency contact created successfully",
      errorMessagePrefix: "Failed to create personnel emergency contact",
    });
  };

export const updatePersonnelEmergencyContact =
  (id, emergencyContactData) => async (dispatch) => {
    const service = getService(serviceNames.perEmergencyContact);
    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_EMERGENCY_CONTACT_REQUEST,
        success: UPDATE_EMERGENCY_CONTACT_SUCCESS,
        failure: UPDATE_EMERGENCY_CONTACT_FAILURE,
      },
      apiCall: () => service.patch(id, emergencyContactData),
      transformResult: (result) => ({
        ...result,
        personnelId: emergencyContactData.personnelId,
        id: id,
      }),
      successMessage: "Personnel emergency contact updated successfully",
      errorMessagePrefix: "Failed to update personnel emergency contact",
    });
  };

export const deletePersonnelEmergenyContact =
  (id, userId, personnelId) => async (dispatch) => {
    const service = getService(serviceNames.perEmergencyContact);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedAt: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: DELETE_EMERGENCY_CONTACT_REQUEST,
        success: DELETE_EMERGENCY_CONTACT_SUCCESS,
        failure: DELETE_EMERGENCY_CONTACT_FAILURE,
      },
      apiCall: () => service.patch(id, patchData),
      transformResult: () => ({ id, personnelId }),
      successMessage: "Emergency contact deleted successfully",
      errorMessagePrefix: "Failed to delete emergency contact",
    });
  };

export const bulkUpdateOrderOfContact = (rows, personnelId, currentUserId) => 
  async (dispatch) => {
    const service = getService(serviceNames.perEmergencyContact);

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: UPDATE_BULK_EMERGENCY_CONTACTS_REQUEST,
        success: UPDATE_BULK_EMERGENCY_CONTACTS_SUCCESS,
        failure: UPDATE_BULK_EMERGENCY_CONTACTS_FAILURE,
      },
      apiCall: async () => await Promise.all(
        rows.map(row =>
          service.patch(
            row.id, 
            {
              orderOfContact: row.orderOfContact,
              updatedBy: currentUserId,
              updatedAt: new Date().toISOString(),
            },
            {
              query: { personnelId: row.personnelId }
            }
          )
        )
      ),
      transformResult: (result) => ({
        personnelId,
        personnelEmergencyContacts: result,
      }),
      errorMessagePrefix: "Failed to update personnel emergency contacts",
    });
  };

export default function personnelEmergencyContactReducer(
  state = initialState,
  action
) {
  switch (action.type) {
    case FETCH_EMERGENCY_CONTACTS_REQUEST:
    case CREATE_EMERGENCY_CONTACT_REQUEST:
    case UPDATE_EMERGENCY_CONTACT_REQUEST:
    case DELETE_EMERGENCY_CONTACT_REQUEST:
    case UPDATE_BULK_EMERGENCY_CONTACTS_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_EMERGENCY_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelEmergencyContacts: {
          ...state.personnelEmergencyContacts,
          [action.payload.personnelId]:
            action.payload.personnelEmergencyContacts,
        },
      };
    case UPDATE_BULK_EMERGENCY_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelEmergencyContacts: {
          [action.payload.personnelId]:
            action.payload.personnelEmergencyContacts,
        },
      };
    case CREATE_EMERGENCY_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelEmergencyContacts: {
          ...state.personnelEmergencyContacts,
          [action.payload.personnelId]: [
            ...(state.personnelEmergencyContacts[action.payload.personnelId] ||
              []),
            action.payload,
          ],
        },
      };
    case UPDATE_EMERGENCY_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelEmergencyContacts: {
          ...state.personnelEmergencyContacts,
          [action.payload.personnelId]: state.personnelEmergencyContacts[
            action.payload.personnelId
          ].map((ec) => (ec.id === action.payload.id ? action.payload : ec)),
        },
      };
    case DELETE_EMERGENCY_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        personnelEmergencyContacts: {
          ...state.personnelEmergencyContacts,
          [action.payload.personnelId]: state.personnelEmergencyContacts[
            action.payload.personnelId
          ].filter((ec) => ec.id !== action.payload.id),
        },
      };
    case FETCH_EMERGENCY_CONTACTS_FAILURE:
    case CREATE_EMERGENCY_CONTACT_FAILURE:
    case UPDATE_EMERGENCY_CONTACT_FAILURE:
    case DELETE_EMERGENCY_CONTACT_FAILURE:
    case UPDATE_BULK_EMERGENCY_CONTACTS_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}
